import { useRef, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { Link } from "react-router-dom";
import { customRenderRichText } from "../utils/renderRichText";

const resolveLink = (link) => {
  if (!link) return { href: "#", isInternal: true };

  switch (link.linktype) {
    case "story":
      return {
        href: `/${link.cached_url?.replace(/^\/+/, "") || ""}`,
        isInternal: true,
      };
    case "url":
    case "asset":
      return {
        href: link.url || link.cached_url || "#",
        isInternal: false,
      };
    case "email":
      return {
        href: `mailto:${link.email || link.cached_url}`,
        isInternal: false,
      };
    default:
      return {
        href: link.cached_url || link.url || "#",
        isInternal: true,
      };
  }
};

const MediaFeature = ({ blok }) => {
  const isReversed = blok.reverse_layout;
  const backgroundImage = blok.background_image?.filename;
  const mediaFile = blok.media?.filename;
  const videoRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  const isVideo = mediaFile?.match(/\.(mp4|webm|ogg)$/i);

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.muted = false;
      videoRef.current.controls = true;
      videoRef.current.play();
      setIsActive(true);
    }
  };

  const { href, isInternal } = resolveLink(blok.button_link);

  const handleInternalNavClick = () => {
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const buttonContent = (
    <>
      {blok.button_icon?.filename && (
        <img
          src={blok.button_icon.filename}
          alt=""
          className="w-[19px] h-[14px] mr-2"
        />
      )}
      {blok.button_text}
    </>
  );

  const buttonClassName =
    "inline-flex items-center bg-[#0061FF] hover:bg-[#004FCC] text-white font-semibold py-3 px-6 rounded-full transition-colors duration-200";

  return (
    <section
      {...storyblokEditable(blok)}
      className="relative flex flex-col md:flex-row items-center justify-between text-white font-['Roboto'] px-6 md:px-12 py-16 overflow-hidden"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50"></div>

      {/* Text */}
      <div
        className={`relative z-10 max-w-xl ${
          isReversed ? "md:order-2 md:pl-12" : "md:order-1 md:pr-12"
        }`}
      >
        {blok.title && (
          <h2 className="text-3xl md:text-3xl font-medium mb-4 leading-tight">
            {blok.title}
          </h2>
        )}

        {blok.body && (
          <div className="text-lg text-gray-200 mb-6 leading-relaxed prose prose-invert max-w-none">
            {typeof blok.body === "string"
              ? blok.body
              : customRenderRichText(blok.body)}
          </div>
        )}

        {blok.button_text &&
          (isInternal ? (
            <Link
              to={href}
              onClick={handleInternalNavClick}
              className={buttonClassName}
            >
              {buttonContent}
            </Link>
          ) : (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClassName}
            >
              {buttonContent}
            </a>
          ))}
      </div>

      {/* Media */}
      {mediaFile && (
        <div
          className={`relative z-10 w-full md:w-1/2 mt-8 md:mt-0 rounded-2xl overflow-hidden shadow-lg ${
            isReversed ? "md:order-1" : "md:order-2"
          }`}
        >
          {isVideo ? (
            <div className="relative w-full h-full">
              <video
                ref={videoRef}
                src={mediaFile}
                autoPlay
                loop={!isActive}
                muted={!isActive}
                playsInline
                className="object-cover w-full h-full rounded-2xl"
              />
              {/* Play Button Overlay */}
              {!isActive && (
                <button
                  onClick={handlePlayClick}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition"
                  aria-label="Play video with sound"
                >
                  <div className="w-20 h-20 border-2 border-white rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="white"
                      viewBox="0 0 24 24"
                      className="w-8 h-8"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </button>
              )}
            </div>
          ) : (
            <img
              src={mediaFile}
              alt={blok.media.alt || "Media feature"}
              className="object-cover w-full h-auto rounded-2xl"
            />
          )}
        </div>
      )}
    </section>
  );
};

export default MediaFeature;